import React, { useState } from 'react';
import { 
  FileSpreadsheet, 
  FileText, 
  Download, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldCheck, 
  Receipt, 
  DollarSign, 
  Percent, 
  Calendar, 
  Building2, 
  Info, 
  Printer, 
  Clock, 
  FileCheck,
  Hash,
  Sparkles,
  Layers,
  ArrowUpRight,
  HelpCircle
} from 'lucide-react';
import { Booking, Expense, ServiceType, BusinessSettings } from '../types';
import { getBusinessSettings } from '../utils/businessSettings';
import { downloadFile } from './ImportExportCenter';
import { getJurisdictionPreset } from '../utils/jurisdictionPresets';

interface AccountantAuditViewProps {
  bookings: Booking[];
  expenses: Expense[];
  streams: ServiceType[];
  settings?: BusinessSettings;
  showToast: (msg: string) => void;
}

function AccountantAuditViewComponent({
  bookings,
  expenses,
  streams,
  settings: propSettings,
  showToast
}: AccountantAuditViewProps) {
  const settings = propSettings || getBusinessSettings();
  const [accountantNotes, setAccountantNotes] = useState<string>(() => {
    return localStorage.getItem('azuraos_cpa_notes') || '';
  });
  const [isExporting, setIsExporting] = useState(false);

  const cur = settings.currencySymbol || 'R';
  const curCode = settings.currencyCode || 'ZAR';
  const vatRate = settings.vatRate !== undefined ? settings.vatRate : 15;
  const taxLabel = settings.taxAuthorityLabel || 'SARS / Tax Authority';
  const companyName = settings.companyName || settings.senderName || 'Operating Business Entity';
  const taxNumber = settings.taxNumber || 'Not Specified';

  // Key Financial Metrics
  const activeBookings = bookings.filter(b => b.status !== 'cancelled');
  const totalGrossRevenue = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const paidBookings = bookings.filter(b => b.status === 'paid');
  const paidRevenue = paidBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const unpaidBookings = bookings.filter(b => (b.status || 'unpaid') === 'unpaid');
  const outstandingRevenue = unpaidBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');

  const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const netProfit = totalGrossRevenue - totalExpenses;
  const marginPct = totalGrossRevenue > 0 ? ((netProfit / totalGrossRevenue) * 100) : 0;

  // Tax calculations
  const outputTax = (totalGrossRevenue * (vatRate / 100));
  const inputTax = (totalExpenses * (vatRate / 100));
  const netTaxLiability = outputTax - inputTax;

  // Audit Integrity Hash Calculation (Deterministic lightweight checksum)
  const checksumSeed = `${bookings.length}:${totalGrossRevenue}:${expenses.length}:${totalExpenses}:${companyName}`;
  const auditChecksum = Array.from(checksumSeed).reduce((hash, char) => {
    return ((hash << 5) - hash) + char.charCodeAt(0) | 0;
  }, 0).toString(16).toUpperCase().replace('-', 'AZ-');

  // Pre-flight validation checks for CPA readiness
  const uncategorizedExpenses = expenses.filter(e => !e.category || e.category === 'other');
  const unpricedBookings = bookings.filter(b => !b.price || b.price <= 0);
  const missingInvoiceNumbers = bookings.filter(b => !b.invoice_number);

  const handleSaveNotes = (val: string) => {
    setAccountantNotes(val);
    localStorage.setItem('azuraos_cpa_notes', val);
  };

  // 1. Export Multi-Table CPA Reconciliation CSV
  const handleExportCPACSV = () => {
    setIsExporting(true);
    try {
      const nowStr = new Date().toISOString();
      const dateStr = nowStr.split('T')[0];

      let csv = '';
      csv += `"AZURAOS STANDARDIZED CPA & AUDIT RECONCILIATION SCHEDULE"\n`;
      csv += `"Company / Entity:","${companyName.replace(/"/g, '""')}"\n`;
      csv += `"Tax Registration / Ref:","${taxNumber.replace(/"/g, '""')}"\n`;
      csv += `"Tax Jurisdiction:","${settings.jurisdiction || 'ZA'}"\n`;
      csv += `"Operating Currency:","${curCode} (${cur})"\n`;
      csv += `"Report Generated Date:","${new Date().toLocaleString()}"\n`;
      csv += `"Audit Integrity Checksum:","${auditChecksum}"\n\n`;

      // Section 1: Executive Financial Position
      csv += `"--- SECTION 1: EXECUTIVE FINANCIAL POSITION & P&L ---"\n`;
      csv += `"Metric","Value (${curCode})","Notes"\n`;
      csv += `"Gross Billed Revenue","${totalGrossRevenue.toFixed(2)}","Across ${bookings.length} recorded client transactions"\n`;
      csv += `"Realized / Paid Inflow","${paidRevenue.toFixed(2)}","${paidBookings.length} paid invoices"\n`;
      csv += `"Outstanding Receivables","${outstandingRevenue.toFixed(2)}","${unpaidBookings.length} uncollected/pending invoices"\n`;
      csv += `"Total Allowable Expenses","${totalExpenses.toFixed(2)}","${expenses.length} deductible entries"\n`;
      csv += `"Net Taxable Operating Income","${netProfit.toFixed(2)}","Pre-tax operating net position"\n`;
      csv += `"Operating Margin","${marginPct.toFixed(2)}%","Net margin percentage"\n\n`;

      // Section 2: Tax Liability Summary
      csv += `"--- SECTION 2: ESTIMATED ${vatRate}% VAT / GST TAX SCHEDULE ---"\n`;
      csv += `"Tax Authority / Filing Dept","${taxLabel.replace(/"/g, '""')}"\n`;
      csv += `"Estimated Output Tax (Collected)","${outputTax.toFixed(2)}"\n`;
      csv += `"Estimated Input Tax (Claimable)","${inputTax.toFixed(2)}"\n`;
      csv += `"Net Estimated Tax Position","${netTaxLiability.toFixed(2)}","${netTaxLiability >= 0 ? 'Payable to Authority' : 'Refund / Credit Due'}"\n\n`;

      // Section 3: Itemized Receivables Register
      csv += `"--- SECTION 3: ITEMIZED RECEIVABLES & REVENUE REGISTER ---"\n`;
      csv += `"Invoice #","Client Name","Client Email","Service Stream","Date","Status","Amount (${curCode})"\n`;
      bookings.forEach((b, idx) => {
        csv += `"${(b.invoice_number || `INV-${String(idx + 1).padStart(4, '0')}`).replace(/"/g, '""')}","${(b.client_name || '').replace(/"/g, '""')}","${(b.client_email || '').replace(/"/g, '""')}","${(b.type || 'General').replace(/"/g, '""')}","${b.date ? new Date(b.date).toLocaleDateString() : ''}","${(b.status || 'unpaid').toUpperCase()}","${Number(b.price || 0).toFixed(2)}"\n`;
      });
      csv += `\n`;

      // Section 4: Itemized Expenses & Deductibles Register
      csv += `"--- SECTION 4: ITEMIZED EXPENSES & DEDUCTIBLE OUTFLOWS ---"\n`;
      csv += `"Expense ID","Date","Category","Description / Note","Linked Stream","Amount (${curCode})"\n`;
      expenses.forEach((e, idx) => {
        csv += `"EXP-${String(idx + 1).padStart(4, '0')}","${e.date ? new Date(e.date).toLocaleDateString() : ''}","${(e.category || 'General').toUpperCase()}","${(e.note || '').replace(/"/g, '""')}","${(e.linked_stream || 'General').replace(/"/g, '""')}","${Number(e.amount || 0).toFixed(2)}"\n`;
      });

      if (accountantNotes) {
        csv += `\n"--- SECTION 5: FOUNDER NOTES & ACCOUNTANT MEMORANDUM ---"\n`;
        csv += `"${accountantNotes.replace(/"/g, '""')}"\n`;
      }

      downloadFile(csv, `azuraos_cpa_audit_pack_${dateStr}.csv`, 'text/csv;charset=utf-8;');
      showToast('Standardized CPA & Loan Audit Pack CSV exported successfully!');
    } catch (err) {
      console.error('Error generating CPA CSV:', err);
      showToast('⚠️ Failed to generate CPA export pack.');
    } finally {
      setIsExporting(false);
    }
  };

  // 2. Generate Printable CPA Financial Statement PDF
  const handlePrintCPAPDF = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) {
      showToast('⚠️ Pop-up blocked. Please allow pop-ups to print the statement.');
      return;
    }

    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    printWin.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>CPA Financial Statement & Pre-Audit Schedule — ${companyName}</title>
          <meta charset="utf-8" />
          <style>
            @page { size: A4; margin: 20mm; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              color: #0F172A;
              line-height: 1.5;
              padding: 24px;
              background: #fff;
            }
            .header-tbl { width: 100%; border-bottom: 2px solid #0F172A; padding-bottom: 14px; margin-bottom: 20px; }
            .header-title { font-size: 20px; font-weight: 800; text-transform: uppercase; color: #0F172A; }
            .sub-title { font-size: 11px; font-weight: 700; color: #0D9488; text-transform: uppercase; letter-spacing: 0.05em; }
            .meta-box { font-size: 11px; color: #475569; }
            .summary-cards { display: flex; gap: 12px; margin-bottom: 24px; }
            .scard { flex: 1; border: 1px solid #CBD5E1; border-radius: 8px; padding: 12px; background: #F8FAFC; }
            .scard-title { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #64748B; margin-bottom: 4px; }
            .scard-val { font-size: 18px; font-weight: 800; font-family: monospace; color: #0F172A; }
            .section-heading { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1.5px solid #CBD5E1; padding-bottom: 4px; margin: 20px 0 10px 0; color: #0F172A; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 16px; }
            th { text-align: left; background: #F1F5F9; padding: 6px 10px; font-weight: 700; border-bottom: 1px solid #CBD5E1; }
            td { padding: 6px 10px; border-bottom: 1px solid #E2E8F0; }
            .totals-row { font-weight: 800; background: #F8FAFC; border-top: 2px solid #0F172A; }
            .checksum-badge { font-family: monospace; font-size: 10px; padding: 4px 8px; background: #F1F5F9; border: 1px dashed #94A3B8; border-radius: 4px; display: inline-block; }
            .signoff-box { margin-top: 36px; border-top: 1px solid #CBD5E1; padding-top: 16px; font-size: 11px; display: flex; justify-content: space-between; }
            .sig-line { width: 220px; border-top: 1px solid #0F172A; margin-top: 36px; padding-top: 4px; text-align: center; font-size: 10px; color: #475569; }
            .no-print { background: #0F172A; color: #fff; padding: 10px 16px; border-radius: 8px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="no-print">
            <span>📄 <strong>CPA Statement Ready:</strong> Click Print to save as PDF or deliver to your external accountant.</span>
            <button onclick="window.print();" style="padding:6px 14px; background:#0D9488; color:#fff; border:none; border-radius:6px; font-weight:bold; cursor:pointer;">Print / Save PDF</button>
          </div>

          <table class="header-tbl">
            <tr>
              <td>
                <div class="sub-title">Pre-Accounting Financial Schedule &amp; Audit Pack</div>
                <div class="header-title">${companyName}</div>
                <div class="meta-box">
                  <strong>Tax Number / Ref:</strong> ${taxNumber} | 
                  <strong>Jurisdiction:</strong> ${settings.jurisdiction || 'ZA'} | 
                  <strong>Currency:</strong> ${curCode} (${cur})
                </div>
              </td>
              <td style="text-align: right; vertical-align: bottom;">
                <div class="meta-box">
                  <strong>Statement Date:</strong> ${dateStr}<br />
                  <span class="checksum-badge">CRC32: ${auditChecksum}</span>
                </div>
              </td>
            </tr>
          </table>

          <!-- Summary Row -->
          <div class="summary-cards">
            <div class="scard">
              <div class="scard-title">Total Revenue Billed</div>
              <div class="scard-val" style="color:#16A34A;">${cur}${totalGrossRevenue.toFixed(2)}</div>
              <div style="font-size:10px; color:#64748B;">Paid: ${cur}${paidRevenue.toFixed(2)} (${paidBookings.length})</div>
            </div>
            <div class="scard">
              <div class="scard-title">Total Deductibles</div>
              <div class="scard-val" style="color:#DC2626;">${cur}${totalExpenses.toFixed(2)}</div>
              <div style="font-size:10px; color:#64748B;">${expenses.length} Itemized Entries</div>
            </div>
            <div class="scard">
              <div class="scard-title">Net Operating Income</div>
              <div class="scard-val" style="color:${netProfit >= 0 ? '#16A34A' : '#DC2626'};">${cur}${netProfit.toFixed(2)}</div>
              <div style="font-size:10px; color:#64748B;">Operating Margin: ${marginPct.toFixed(1)}%</div>
            </div>
            <div class="scard">
              <div class="scard-title">Est. Net Tax Liability</div>
              <div class="scard-val">${cur}${netTaxLiability.toFixed(2)}</div>
              <div style="font-size:10px; color:#64748B;">@ ${vatRate}% Rate (${taxLabel})</div>
            </div>
          </div>

          <!-- Section 1: Income Breakdown -->
          <div class="section-heading">1. Client Revenue &amp; Receivables Register (${bookings.length} Records)</div>
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client / Counterparty</th>
                <th>Service Stream</th>
                <th>Date</th>
                <th>Status</th>
                <th style="text-align:right;">Amount (${cur})</th>
              </tr>
            </thead>
            <tbody>
              ${bookings.map((b, i) => `
                <tr>
                  <td style="font-family:monospace;">${b.invoice_number || `INV-${String(i+1).padStart(4, '0')}`}</td>
                  <td><strong>${b.client_name || 'Client'}</strong></td>
                  <td>${b.type || 'Service'}</td>
                  <td>${b.date ? new Date(b.date).toLocaleDateString() : '—'}</td>
                  <td><span style="font-weight:700; color:${b.status === 'paid' ? '#16A34A' : '#D97706'};">${(b.status || 'unpaid').toUpperCase()}</span></td>
                  <td style="text-align:right; font-family:monospace;">${cur}${Number(b.price || 0).toFixed(2)}</td>
                </tr>
              `).join('') || '<tr><td colspan="6" style="text-align:center; color:#64748B;">No recorded bookings.</td></tr>'}
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td colspan="5">Total Gross Billed Revenue</td>
                <td style="text-align:right; font-family:monospace; color:#16A34A;">${cur}${totalGrossRevenue.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>

          <!-- Section 2: Expense Breakdown -->
          <div class="section-heading">2. Allowable Operational Outflows &amp; Expenses (${expenses.length} Records)</div>
          <table>
            <thead>
              <tr>
                <th>Item #</th>
                <th>Description / Vendor</th>
                <th>Category</th>
                <th>Date</th>
                <th style="text-align:right;">Amount (${cur})</th>
              </tr>
            </thead>
            <tbody>
              ${expenses.map((e, i) => `
                <tr>
                  <td style="font-family:monospace;">EXP-${String(i+1).padStart(4, '0')}</td>
                  <td><strong>${e.note || 'Expense'}</strong></td>
                  <td>${(e.category || 'General').toUpperCase()}</td>
                  <td>${e.date ? new Date(e.date).toLocaleDateString() : '—'}</td>
                  <td style="text-align:right; font-family:monospace; color:#DC2626;">${cur}${Number(e.amount || 0).toFixed(2)}</td>
                </tr>
              `).join('') || '<tr><td colspan="5" style="text-align:center; color:#64748B;">No recorded expenses.</td></tr>'}
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td colspan="4">Total Allowable Expenses</td>
                <td style="text-align:right; font-family:monospace; color:#DC2626;">${cur}${totalExpenses.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>

          ${accountantNotes ? `
            <div class="section-heading">3. Founder Memorandum to External Accountant / Underwriter</div>
            <div style="font-size:11px; color:#334155; background:#F8FAFC; border:1px solid #E2E8F0; border-radius:6px; padding:10px; margin-bottom:16px;">
              ${accountantNotes.replace(/\n/g, '<br />')}
            </div>
          ` : ''}

          <!-- Sign-Off Block -->
          <div class="signoff-box">
            <div>
              <div style="font-weight:700;">Prepared by Business Owner:</div>
              <div class="sig-line">Founder / Managing Director Signature</div>
            </div>
            <div>
              <div style="font-weight:700;">Reviewed by CPA / Accounting Professional:</div>
              <div class="sig-line">Certified CPA / CA(SA) Sign-off &amp; Date</div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWin.document.close();
  };

  const preset = getJurisdictionPreset(settings.jurisdiction);
  const vatThreshold = preset.vatThresholdAmount || 1000000;
  const isUnderThreshold = totalGrossRevenue < vatThreshold;
  const thresholdPercentage = Math.min(100, (totalGrossRevenue / vatThreshold) * 100);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Banner */}
      <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-6 relative overflow-hidden shadow-xl">
        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-400 text-[10px] font-mono font-bold uppercase tracking-wider">
                Pre-Accounting &amp; Institutional Audit Deck
              </span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                isUnderThreshold ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
              }`}>
                {isUnderThreshold ? `0% Safe Harbor (${preset.name})` : 'Standard Filing'}
              </span>
              <span className="px-2 py-0.5 rounded bg-[#0C0E0F] border border-[#2A2F31] text-gray-400 text-[10px] font-mono">
                Checksum: <strong className="text-teal-400">{auditChecksum}</strong>
              </span>
            </div>
            <h2 className="text-xl font-bold font-display text-white">
              Accountant &amp; CPA Audit View
            </h2>
            <p className="text-xs text-gray-400 max-w-2xl leading-relaxed">
              Standardized financial summaries, accounts receivable registers, and deductible schedules designed specifically to be handed over to certified accountants, tax advisors, or bank loan underwriters.
            </p>
          </div>

          {/* Action Export Buttons */}
          <div className="flex items-center gap-2.5 w-full md:w-auto shrink-0 flex-wrap">
            <button
              onClick={handleExportCPACSV}
              disabled={isExporting}
              className="flex-1 md:flex-none px-4 py-2.5 bg-[#0C0E0F] hover:bg-[#1A1D1E] border border-[#2A2F31] hover:border-[#14B8A6]/40 text-[#14B8A6] text-xs font-bold rounded-xl transition flex items-center justify-center gap-2 cursor-pointer shadow-sm active:scale-95"
            >
              <FileSpreadsheet size={15} />
              <span>Export CPA CSV</span>
            </button>

            <button
              onClick={handlePrintCPAPDF}
              className="flex-1 md:flex-none px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-blue-600/20 active:scale-95"
            >
              <Printer size={15} />
              <span>Print Audit PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statutory Safe Harbor Milestone Banner */}
      <div className="p-4 bg-[#0F1415] border border-teal-500/20 rounded-2xl space-y-2 text-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
          <div className="flex items-center gap-2 font-bold text-white">
            <span className="text-base">{preset.flag}</span>
            <span>{preset.thresholdMilestoneLabel}</span>
            <span className="px-2 py-0.5 rounded text-[10px] bg-teal-500/20 text-teal-300 font-mono">
              {isUnderThreshold ? '0% Tax Safe Harbor' : 'Threshold Exceeded'}
            </span>
          </div>
          <div className="text-gray-400 text-[11px] font-mono">
            {cur}{totalGrossRevenue.toFixed(0)} / {cur}{vatThreshold.toLocaleString()} ({thresholdPercentage.toFixed(1)}%)
          </div>
        </div>
        <div className="w-full bg-[#182022] h-2 rounded-full overflow-hidden">
          <div 
            className="bg-gradient-to-r from-emerald-500 to-teal-400 h-full rounded-full transition-all"
            style={{ width: `${Math.max(3, thresholdPercentage)}%` }}
          />
        </div>
        <div className="text-[11px] text-gray-400 flex items-center justify-between">
          <span>{preset.vatThresholdFormatted}</span>
          <span className="text-teal-400 font-medium">
            {isUnderThreshold ? `Safe Harbor Active: ${cur}${(vatThreshold - totalGrossRevenue).toLocaleString()} headroom` : 'Mandatory Registration Reached'}
          </span>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Gross Revenue */}
        <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
          <div className="text-[10px] uppercase font-bold text-gray-400">Total Billed Revenue</div>
          <div className="text-lg font-bold font-mono text-emerald-400">{cur}{totalGrossRevenue.toFixed(2)}</div>
          <div className="text-[10px] text-gray-500">
            {paidBookings.length} Paid ({cur}{paidRevenue.toFixed(0)}) &bull; {unpaidBookings.length} Pending
          </div>
        </div>

        {/* Total Expenses */}
        <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
          <div className="text-[10px] uppercase font-bold text-gray-400">Allowable Deductibles</div>
          <div className="text-lg font-bold font-mono text-rose-400">{cur}{totalExpenses.toFixed(2)}</div>
          <div className="text-[10px] text-gray-500">{expenses.length} Deductible entries recorded</div>
        </div>

        {/* Net Profit */}
        <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
          <div className="text-[10px] uppercase font-bold text-gray-400">Net Taxable Operating Profit</div>
          <div className={`text-lg font-bold font-mono ${netProfit >= 0 ? 'text-teal-400' : 'text-rose-400'}`}>
            {cur}{netProfit.toFixed(2)}
          </div>
          <div className="text-[10px] text-gray-500">Operating Net Margin: <strong>{marginPct.toFixed(1)}%</strong></div>
        </div>

        {/* Tax Buffer */}
        <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
          <div className="text-[10px] uppercase font-bold text-gray-400">Est. Net Tax Liability ({vatRate}%)</div>
          <div className="text-lg font-bold font-mono text-amber-400">{cur}{netTaxLiability.toFixed(2)}</div>
          <div className="text-[10px] text-gray-500">Output: {cur}{outputTax.toFixed(0)} | Input: {cur}{inputTax.toFixed(0)}</div>
        </div>
      </div>

      {/* Pre-Flight CPA Audit Checklist */}
      <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-xl space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-300 flex items-center gap-2">
          <ShieldCheck size={16} className="text-teal-400" />
          Pre-Flight Data Health Checklist (CPA Readiness)
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-lg flex items-center justify-between">
            <span className="text-gray-300">Uncategorized Expenses</span>
            {uncategorizedExpenses.length === 0 ? (
              <span className="text-emerald-400 font-bold flex items-center gap-1"><CheckCircle2 size={13} /> 0 items (Clean)</span>
            ) : (
              <span className="text-amber-400 font-bold flex items-center gap-1"><AlertTriangle size={13} /> {uncategorizedExpenses.length} to review</span>
            )}
          </div>

          <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-lg flex items-center justify-between">
            <span className="text-gray-300">Unpriced / Zero Bookings</span>
            {unpricedBookings.length === 0 ? (
              <span className="text-emerald-400 font-bold flex items-center gap-1"><CheckCircle2 size={13} /> 0 items (Clean)</span>
            ) : (
              <span className="text-rose-400 font-bold flex items-center gap-1"><AlertTriangle size={13} /> {unpricedBookings.length} unpriced</span>
            )}
          </div>

          <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-lg flex items-center justify-between">
            <span className="text-gray-300">Tax Number Specified</span>
            {settings.taxNumber ? (
              <span className="text-emerald-400 font-bold flex items-center gap-1"><CheckCircle2 size={13} /> Configured</span>
            ) : (
              <span className="text-amber-400 font-bold flex items-center gap-1"><AlertTriangle size={13} /> Missing in Settings</span>
            )}
          </div>
        </div>
      </div>

      {/* Founder Notes to Accountant */}
      <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-xl space-y-2">
        <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider">
          Founder Working Notes &amp; Clarifications (Appears on Export Schedule)
        </label>
        <textarea
          rows={3}
          value={accountantNotes}
          onChange={(e) => handleSaveNotes(e.target.value)}
          placeholder="e.g. Include note about advance retainer from client Jane Doe for Q3 deliverables, or home office deduction notes..."
          className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-mono leading-relaxed"
        />
        <p className="text-[10px] text-gray-500">
          Saved automatically to local workspace. Handed over seamlessly when exporting CSV or PDF statement.
        </p>
      </div>
    </div>
  );
}

export const AccountantAuditView = React.memo(AccountantAuditViewComponent);

